import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  forceCenter,
  forceCollide,
  forceLink,
  forceManyBody,
  forceSimulation,
  forceX,
  forceY,
  type ForceCollide,
  type Simulation,
  type SimulationLinkDatum,
  type SimulationNodeDatum,
} from 'd3-force';
import type { AiOutputFormat, AnalysisDisplayRules, FieldDef } from '@citizen-walk/shared-types';
import { useDistrictReady } from '../district';
import { useAiCategoryMasters } from '../posts';
import type { IdeaMinimal, InsightMinimal } from '../insights';
import { useEcwIdeas, useEcwInsights } from '../insights';
import type { PostMinimal } from '../posts';
import { AxisFilterPanel, useAxisFilter } from './analysisFilter';
import type { AxisGroup } from './matrixAxisModel';
import { calculateTextSimilarity } from './textAnalysis';
import {
  collectCategoryMajorValues,
  collectEcwValues,
  resolveCategoryMajorColor,
  resolveCategoryMajorLabel,
  resolveEmotionColor,
} from './analysisAxes';
import type { AnalysisPerspective } from './analysisPerspectives';

/**
 * 分析アプリ「ネットワーク」タブ — 3 層ナレッジ・ネットワーク
 * (docs/107 Wave 6、old2 `components/ecw/NetworkGraphView.tsx` 1551 行を正に parity)。
 *
 * old2 は `react-force-graph-2d` (canvas) だが、v2 は SVG + d3-force で踏襲する
 * (jsdom/headless 検証が可能、共有 d3-force 1 本で完結)。意味色 (8 色 post palette /
 * 3 色 insight palette / 象限色) は old2 をそのまま保持し、UI シェルは v2 semantic token。
 *
 * Wave 6 で追加した機能 (old2 parity):
 *   - **ズーム / パン + 全体表示(fit)**: `<g transform=...>` を wheel / 2 本指ピンチ / 背景ドラッグで操作、
 *     「🔍 全体表示」でノード bbox に fit (old2 zoomToFit / centerAt / zoom)。
 *   - **サイドコントロールパネル (折りたたみ ☰)**: 全コントロールを右上パネルに集約 (old2 L1081)。
 *   - **投稿ノード 8 色分け + 凡例**: 感情 / Happy×Health 象限 / 対象大分類 / チーム / ニックネーム /
 *     scope / duration / 投稿者 (old2 postColorMode 8 種、データ駆動で出現分のみ)。
 *   - **気づきノード 3 色分け**: デフォルト緑 / 感情 / センチメント (old2 insightColorMode)。
 *   - **ノードクリック → 中心ズーム + 詳細**: 投稿は onSelectPost、それ以外は中心へ pan-zoom +
 *     パネル下部に詳細表示 (old2 handleNodeClick + selectedNode panel)。
 *   - **孤立投稿 既定非表示トグル**: 気づきと繋がらない投稿を既定で隠す (old2 hideIsolatedPosts, 初期 true)。
 *   - **日付フィルタ**: createdAt で post / insight / idea を連鎖フィルタ (old2 L206-255)。
 *   - **次数 (degree) サイズ + 倍率スライダー**: リンク数 × 倍率でノードサイズ (old2 getCustomVal)。
 *   - **強調フィルタ + 引力調整**: team / nickname の select で対象外を減光、引力スライダーで
 *     同属性クラスタリング (forceX/forceY、old2 gravity*)。
 *   - **ノード形状**: 投稿 = ■ / 気づき = ◆ / 処方箋 = ● (old2 nodeCanvasObject)。
 *   - **ノード内画像**: 投稿写真 / 処方箋キャッチ画像を SVG `<image>` でクリップ表示 (old2 imgObj)。
 *   - **結実投稿 強調**: 処方箋まで繋がった投稿だけ強調 + 処方箋数でサイズアップ (old2 highlightIdeaPosts)。
 *   - **使い方マニュアル (折りたたみ ❓)**: 基本操作 / 凡例 / 便利機能 (old2 showManual modal)。
 *   - **テストデータ除外トグル**: title/nickname に「テスト」「test」を含むデータを除外 (old2 L264-268)。
 *
 * 判断補正 (docs/107 §判断補正):
 *   - Happy/Health は型付きフィールドでなく `post.extraFields.HappinessScore/HealthScore` から読む
 *     (v2 思想維持、heatmap/score view と同経路)。
 *   - 感情色 / 大分類色は M2 master colorMap (`resolveEmotionColor` / `resolveCategoryMajorColor`) で
 *     解決し、matrix / 地図 / 一覧と一貫させる (master 未解決時は palette fallback)。
 *   - old2「処方箋に戻る」「通常の分析サイトに戻る」hash ナビは v2 タブ UI に無いため非移植。
 *
 * Wave 2 keep-alive 維持: 'analysisTabActivated' 受信で resize 反応 (固定 SVG なので no-op だが
 * fit 状態を初期化しない)。Wave6-critical: 類似度リンクは **気づき (insight) 同士** (old2 L469-479)。
 *
 * 制約:
 *   - d3-force simulation はトグル ON 時のみ作動 (静的 3-column レイアウトが既定、テスト互換)。
 *   - 画像は PostMinimal.photoThumbUrl/photoUrl (resolved download URL) を直接 `<image href>` に使用。
 */

/** ネットワーク表示の表示位置 (k=拡大率、x/y=平行移動)。 */
export interface NetworkViewState {
  k: number;
  x: number;
  y: number;
}

/** svg の画面上の矩形 (`getBoundingClientRect()` の必要部分)。 */
export interface LayoutRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** 画面上の点 (ポインタ / 指の位置)。 */
export interface ClientPoint {
  clientX: number;
  clientY: number;
}

const ZOOM_MIN = 0.2;
const ZOOM_MAX = 8;
const clampZoom = (k: number) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, k));

/**
 * 画面座標 → viewBox 座標 (pure)。
 *
 * svg は `preserveAspectRatio` 既定 (xMidYMid meet) で描かれるため、コンテナと viewBox の縦横比が
 * 違うと上下または左右に余白 (レターボックス) が付く。その余白を差し引いて換算する。
 * `scale` は viewBox 1 単位あたりの画面 px。幅/高さ 0 (非表示中など) では割り算が壊れるので
 * 原点・等倍を返す。
 */
export function clientToLayout(
  rect: LayoutRect,
  layoutW: number,
  layoutH: number,
  clientX: number,
  clientY: number,
): { x: number; y: number; scale: number } {
  if (!rect.width || !rect.height || !layoutW || !layoutH) return { x: 0, y: 0, scale: 1 };
  const scale = Math.min(rect.width / layoutW, rect.height / layoutH);
  const offX = (rect.width - layoutW * scale) / 2;
  const offY = (rect.height - layoutH * scale) / 2;
  return {
    x: (clientX - rect.left - offX) / scale,
    y: (clientY - rect.top - offY) / scale,
    scale,
  };
}

/** 図形座標 (wx, wy) が viewBox 座標 (px, py) の直下に来るように、拡大率 k での表示位置を決める。 */
function viewAnchoredAt(
  k: number,
  px: number,
  py: number,
  wx: number,
  wy: number,
): NetworkViewState {
  return { k, x: px - wx * k, y: py - wy * k };
}

/** ホイールズームの入力 (イベントからハンドラ内で取り出した数値のみ)。 */
export interface WheelZoomInput {
  rect: LayoutRect;
  clientX: number;
  clientY: number;
  deltaY: number;
  layoutW: number;
  layoutH: number;
}

/**
 * ホイールズーム後の表示位置を求める (pure、2026-09-05)。
 *
 * ポインタ位置を中心に拡大縮小する。拡大率は 0.2〜8 に収める。
 * イベントオブジェクトを受け取らないのは、setState の更新関数の中で合成イベントを読むと
 * `currentTarget` が null になっていて落ちるため (呼び出し側で数値に落としてから渡す)。
 */
export function computeWheelZoom(view: NetworkViewState, input: WheelZoomInput): NetworkViewState {
  const { rect, clientX, clientY, deltaY, layoutW, layoutH } = input;
  const factor = deltaY < 0 ? 1.15 : 1 / 1.15;
  const k = clampZoom(view.k * factor);
  if (!rect.width || !rect.height) return { ...view, k };
  const p = clientToLayout(rect, layoutW, layoutH, clientX, clientY);
  const wx = (p.x - view.x) / view.k;
  const wy = (p.y - view.y) / view.k;
  return viewAnchoredAt(k, p.x, p.y, wx, wy);
}

/** パン (背景ドラッグ) 開始時の記録。 */
export interface PanStart {
  pointerId: number;
  start: ClientPoint;
  view: NetworkViewState;
}

/** パン後の表示位置 (pure)。ポインタの移動量を viewBox 単位に換算して開始時の位置に足す。 */
export function computePan(
  start: PanStart,
  current: ClientPoint,
  rect: LayoutRect,
  layoutW: number,
  layoutH: number,
): NetworkViewState {
  const { scale } = clientToLayout(rect, layoutW, layoutH, current.clientX, current.clientY);
  return {
    ...start.view,
    x: start.view.x + (current.clientX - start.start.clientX) / scale,
    y: start.view.y + (current.clientY - start.start.clientY) / scale,
  };
}

/** ピンチ (2 本指) 開始時の記録: 指の距離と中点、そのときの表示位置。 */
export interface PinchStart {
  pointerIds: [number, number];
  dist: number;
  mid: ClientPoint;
  view: NetworkViewState;
}

export function pinchStartFrom(
  pointerIds: [number, number],
  a: ClientPoint,
  b: ClientPoint,
  view: NetworkViewState,
): PinchStart {
  return {
    pointerIds,
    dist: Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY),
    mid: { clientX: (a.clientX + b.clientX) / 2, clientY: (a.clientY + b.clientY) / 2 },
    view,
  };
}

/**
 * ピンチ後の表示位置 (pure、2026-09-05)。
 *
 * 指の距離の比で拡大率を変え、開始時の中点の直下にあった図形が現在の中点の直下に来るように
 * 位置を合わせる (拡大縮小と移動を同時に扱うので、指を広げながらずらしても追従する)。
 * 拡大率は 0.2〜8 に収める。
 */
export function computePinchZoom(
  start: PinchStart,
  a: ClientPoint,
  b: ClientPoint,
  rect: LayoutRect,
  layoutW: number,
  layoutH: number,
): NetworkViewState {
  const dist = Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
  const ratio = start.dist > 0 ? dist / start.dist : 1;
  const k = clampZoom(start.view.k * ratio);
  if (!rect.width || !rect.height) return { ...start.view, k };
  const m0 = clientToLayout(rect, layoutW, layoutH, start.mid.clientX, start.mid.clientY);
  const m1 = clientToLayout(
    rect,
    layoutW,
    layoutH,
    (a.clientX + b.clientX) / 2,
    (a.clientY + b.clientY) / 2,
  );
  const wx = (m0.x - start.view.x) / start.view.k;
  const wy = (m0.y - start.view.y) / start.view.k;
  return viewAnchoredAt(k, m1.x, m1.y, wx, wy);
}

export interface AnalysisNetworkGraphViewProps {
  /** docs/29 §8-1: 追加の AI 解析の観点 (軸の候補に観点ごとの AI 解析の軸を足す)。 */
  perspectives?: readonly AnalysisPerspective[];
  /** 既定の観点の表示名 (追加の観点があるとき軸のラベルに付ける)。 */
  defaultPerspectiveLabel?: string | null;
  districtId: string;
  posts: PostMinimal[];
  onSelectPost?: (post: PostMinimal) => void;
  /** 気づきノードを選んだときの詳細表示 (投稿と同じく詳細を開く、2026-09-07)。 */
  onSelectInsight?: (insight: InsightMinimal) => void;
  /** 処方箋ノードを選んだときの詳細表示。 */
  onSelectIdea?: (idea: IdeaMinimal) => void;
  /** Sprint AV: M5 network rules (未指定なら hardcoded default で動作)。 */
  networkRules?: AnalysisDisplayRules['network'];
  /* === 軸駆動フィルタ + 動的色分け軸 (マトリクスと同じ matrixAxisModel エンジン) === */
  /** dataSettings.postFields 由来軸。 */
  postFields?: FieldDef[];
  /** M5 マトリクスルール (custom 軸 / hiddenAxisKeys / includeEmpty)。 */
  matrixRules?: AnalysisDisplayRules['matrix'];
  /** M3 出力フォーマット (format 駆動の軸 code 解決)。 */
  outputFormat?: AiOutputFormat | null;
}

type NodeType = 'post' | 'insight' | 'idea';

// 投稿ノードの色分け: 固定モード ('emotion'/'quadrant'/'categoryMajor'/'team'/'nickname'/
// 'scope'/'duration') に加え、matrixAxisModel 由来の動的軸キーも値として受け付ける (string)。
type InsightColorMode = 'default' | 'emotion' | 'sentiment';

interface GraphNode extends SimulationNodeDatum {
  id: string;
  type: NodeType;
  label: string;
  description: string;
  /** 初期 x (3-column layout で算出、simulation の warm start に使用)。 */
  initialX: number;
  initialY: number;
  /** ベース色 (emotion 既定 / insight 緑 / idea 大分類色)。 */
  baseColor: string;
  /** ベースサイズ (post=5 / insight=10 / idea=15 or 25)。 */
  baseVal: number;
  /** 強調フィルタ用属性。 */
  team: string;
  nickname: string;
  /** 結実 (この投稿が紐づく処方箋数)。post のみ。 */
  ideaCount: number;
  /** 紐づくリンク数 (degree サイズ用)。 */
  degree: number;
  /** insight が紐づく post / idea のリンク数 (insight の degree サイズ用)。 */
  insightPostLinks: number;
  insightIdeaLinks: number;
  /** ノード内画像 (resolved URL、なければ null)。 */
  imageUrl: string | null;
  postRef?: PostMinimal;
  insightRef?: InsightMinimal;
  ideaRef?: IdeaMinimal;
}

interface GraphLink extends SimulationLinkDatum<GraphNode> {
  source: string | GraphNode;
  target: string | GraphNode;
  kind: 'idea-insight' | 'insight-post' | 'similarity';
  weight?: number;
}

const NODE_RADIUS_FOCUS_BONUS = 4;
const COL_WIDTH = 240;
const ROW_HEIGHT_BASE = 36;
const MARGIN_TOP = 56;
const SVG_PADDING_X = 24;
const COL_LEFT_X = SVG_PADDING_X + COL_WIDTH * 0;
const COL_MID_X = SVG_PADDING_X + COL_WIDTH * 1;
const COL_RIGHT_X = SVG_PADDING_X + COL_WIDTH * 2;
/** 整列(3列) の列見出し (y=28, 12px) の下端。大きなノードが見出しに被らないよう最初の行を下げる基準。 */
const COLUMN_HEADER_BOTTOM = 34;
/** 整列(3列) で隣り合うノードの図形どうしに最低限空ける隙間 (px)。 */
const COLUMN_NODE_GAP = 6;
/** 円状(放射状) の衝突判定で図形半径に足す余白 (px)。 */
const COLLIDE_PADDING = 3;
/** 円状(放射状) の衝突半径の下限 (px)。小さいノードどうしの間隔は従来 (forceCollide(16)) と同じに保つ。 */
const COLLIDE_MIN_RADIUS = 16;
/** ひし形 (気づき) の対角線半分 = 表示半径 × この倍率 (NodeShape と揃える)。 */
const INSIGHT_DIAMOND_SCALE = 1.3;

/** 図形の縦方向の半幅 (整列(3列) の行間用)。ひし形は対角線ぶん大きい。 */
const verticalHalfExtent = (type: NodeType, r: number): number =>
  type === 'insight' ? r * INSIGHT_DIAMOND_SCALE : r;
/** 図形を包む円の半径 (衝突判定用)。四角は対角 r√2、ひし形は対角線半分、円は r。 */
const boundingRadius = (type: NodeType, r: number): number => {
  if (type === 'post') return r * Math.SQRT2;
  if (type === 'insight') return r * INSIGHT_DIAMOND_SCALE;
  return r;
};
/** 衝突半径: 図形を包む円 + 余白、ただし従来の 16px を下回らない。 */
const collideRadiusOf = (type: NodeType, r: number): number =>
  Math.max(COLLIDE_MIN_RADIUS, boundingRadius(type, r) + COLLIDE_PADDING);

// === old2 意味色 (semantic token 化しない、color *meaning* を保持) =====================
const POST_BASE_COLOR = '#0ea5e9'; // 感情未解決時の post fallback (slate/sky)
const INSIGHT_DEFAULT_COLOR = '#4CAF50'; // 気づき デフォルト緑 (old2 L343)
const IDEA_BASE_COLOR = '#f59e0b'; // 処方箋 大分類未解決時 fallback (amber)
const LINK_IDEA_INSIGHT_COLOR = '#fb923c';
const LINK_INSIGHT_POST_COLOR = '#60a5fa';
const LINK_SIMILARITY_COLOR = '#0ea5e9';
const LINK_DIMMED_COLOR = '#cbd5e1';
const DIMMED_NODE_FILL = 'rgba(148, 163, 184, 0.35)';

/** old2 getQuadrantColor (L29-35) 逐語 — Happy×Health 象限色。 */
function getQuadrantColor(happy: number, healthy: number): string {
  if (happy > 0 && healthy > 0) return '#FF69B4';
  if (happy > 0 && healthy < 0) return '#9C27B0';
  if (happy < 0 && healthy > 0) return '#00BCD4';
  if (happy < 0 && healthy < 0) return '#2196F3';
  return '#9E9E9E';
}

const QUADRANT_LEGEND: Array<{ label: string; color: string }> = [
  { label: 'Happy>0, Healthy>0', color: '#FF69B4' },
  { label: 'Happy>0, Healthy<0', color: '#9C27B0' },
  { label: 'Happy<0, Healthy>0', color: '#00BCD4' },
  { label: 'Happy<0, Healthy<0', color: '#2196F3' },
  { label: 'Neutral (0)', color: '#9E9E9E' },
];

/** old2 stringToColor (L37-44) 逐語 — 任意文字列を安定した色に。 */
function stringToColor(str: string): string {
  if (!str) return '#9E9E9E';
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const c = (hash & 0x00ffffff).toString(16).toUpperCase();
  return '#' + '00000'.substring(0, 6 - c.length) + c;
}

/** old2 insightColorMode='sentiment' (L801-807) 逐語 — sentiment -1..1 を赤〜青に。 */
function sentimentRgb(s: number): string {
  const r = s > 0 ? 255 : Math.floor(200 + 100 * s);
  const g = Math.floor(200 - 100 * Math.abs(s));
  const b = s < 0 ? 255 : Math.floor(200 - 100 * s);
  return `rgb(${r},${g},${b})`;
}

/** extraFields の数値読取 (heatmap/score view と同じ規約)。 */
function readNumber(extraFields: Record<string, unknown> | null, key: string): number | null {
  if (!extraFields) return null;
  const v = extraFields[key];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** extraFields の文字列読取 (scope / duration / team / gender 等の任意属性)。 */
function readString(extraFields: Record<string, unknown> | null, keys: string[]): string {
  if (!extraFields) return '';
  for (const key of keys) {
    const v = extraFields[key];
    if (typeof v === 'string' && v.trim() !== '') return v.trim();
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  }
  return '';
}

const HAPPY_KEYS = ['HappinessScore', 'happinessScore', 'happy', 'Happy'];
const HEALTH_KEYS = ['HealthScore', 'healthScore', 'healthy', 'Healthy', 'Health'];
const SCOPE_KEYS = ['scope', 'Scope', '主体範囲', 'subjectScope'];
const DURATION_KEYS = ['duration', 'Duration', '効果時間', '持続性'];
const TEAM_KEYS = ['team', 'group', 'user_group', 'チーム'];

function happyOf(p: PostMinimal): number {
  for (const k of HAPPY_KEYS) {
    const v = readNumber(p.extraFields, k);
    if (v !== null) return v;
  }
  return 0;
}
function healthOf(p: PostMinimal): number {
  for (const k of HEALTH_KEYS) {
    const v = readNumber(p.extraFields, k);
    if (v !== null) return v;
  }
  return 0;
}

const isTestText = (text: string | null | undefined): boolean =>
  !!text && (text.includes('テスト') || text.toLowerCase().includes('test'));

/** Timestamp / string → YYYY-MM-DD。 */
function toDateStr(ts: { toDate?: () => Date } | null | undefined): string {
  if (!ts || typeof ts.toDate !== 'function') return '';
  try {
    const d = ts.toDate();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  } catch {
    return '';
  }
}

// docs/107 Wave6 / old2 NetworkGraphView L469-479: 気づき同士の類似度リンク既定閾値 (old2 固定 0.4)。
const DEFAULT_SIMILARITY_THRESHOLD = 0.4;
const SIMILARITY_MAX_LINKS = 15;

const TYPE_LABEL: Record<NodeType, string> = { post: '投稿', insight: '気づき', idea: '処方箋' };

export function AnalysisNetworkGraphView({
  perspectives,
  defaultPerspectiveLabel,
  districtId,
  posts,
  onSelectPost,
  onSelectInsight,
  onSelectIdea,
  networkRules,
  postFields,
  matrixRules,
  outputFormat,
}: AnalysisNetworkGraphViewProps) {
  const district = useDistrictReady();
  const resolver = useAiCategoryMasters(district.aiAnalysisSettingsId ?? null);
  const { state: insightsState } = useEcwInsights(districtId);
  const { state: ideasState } = useEcwIdeas(districtId);

  // 軸駆動フィルタ (master/dataSettings/M5 由来) + 動的色分け軸。マトリクス/分析画面と同じ engine。
  const axisFilter = useAxisFilter(posts, {
    resolver,
    postFields: postFields ?? district.dataSettings?.postFields,
    matrixRules,
    outputFormat,
    perspectives,
    defaultPerspectiveLabel,
  });
  const [showFilters, setShowFilters] = useState(false);
  const axisFilteredPostIds = useMemo(
    () => new Set(axisFilter.filteredPosts.map((p) => p.id)),
    [axisFilter.filteredPosts],
  );
  const axisFilterActiveCount = axisFilter.activeFilterCount;

  const [focusId, setFocusId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showSimilarity, setShowSimilarity] = useState(false);
  // 既定は円状(放射状)レイアウト (ユーザー指示)。整列(3列)はヘッダの切替で選択。
  const [simulationEnabled, setSimulationEnabled] = useState(true);
  const [frozen, setFrozen] = useState(false);
  // 固定中かどうかを effect / イベントハンドラから同期的に読むための鏡。
  // simulation effect の依存に `frozen` を入れると、チェックのたびに配置が円周から作り直されて
  // しまうため (2026-09-05 報告)、effect は state ではなくこの ref を見る。
  const frozenRef = useRef(false);

  // パネル / マニュアル
  const [panelOpen, setPanelOpen] = useState(true);
  const [showManual, setShowManual] = useState(false);

  // 色分け
  // 'emotion'/'quadrant'/'categoryMajor'/'team'/'nickname'/'scope'/'duration' の固定モードに加え、
  // matrixAxisModel 由来の動的軸キー (例 'category.major' / 'postField:X' / custom 軸) も受け付ける。
  const [postColorMode, setPostColorMode] = useState<string>('emotion');
  const [insightColorMode, setInsightColorMode] = useState<InsightColorMode>('default');

  // 表示トグル
  const [hideIsolatedPosts, setHideIsolatedPosts] = useState(true); // old2 初期 true
  const [excludeTestData, setExcludeTestData] = useState(true); // old2 は常時除外、v2 はトグル化
  const [highlightIdeaPosts, setHighlightIdeaPosts] = useState(false);
  const [showImage, setShowImage] = useState(false);

  // 日付フィルタ
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // 次数サイズ
  const [useDegreeSize, setUseDegreeSize] = useState(false);
  const [degreeMultiplier, setDegreeMultiplier] = useState(0.3);

  // 強調フィルタ + 引力
  const [highlightTeam, setHighlightTeam] = useState('');
  const [highlightNickname, setHighlightNickname] = useState('');
  const [gravityTeam, setGravityTeam] = useState(0);
  const [gravityNickname, setGravityNickname] = useState(0);

  // ズーム / パン。viewRef は「いま画面に出ている表示位置」をイベントハンドラから同期的に読むための鏡
  // (ピンチ開始時など、直前のパンがまだ再レンダーされていなくても最新値で続きを計算できる)。
  const [view, setView] = useState<NetworkViewState>({ k: 1, x: 0, y: 0 });
  const viewRef = useRef<NetworkViewState>({ k: 1, x: 0, y: 0 });
  useEffect(() => {
    viewRef.current = view;
  }, [view]);
  const applyView = useCallback((next: NetworkViewState) => {
    viewRef.current = next;
    setView(next);
  }, []);

  // M5 反映
  const similarityThreshold = useMemo(() => {
    const v = networkRules?.similarityThreshold;
    if (typeof v !== 'number' || !Number.isFinite(v)) return DEFAULT_SIMILARITY_THRESHOLD;
    return Math.min(1, Math.max(0, v));
  }, [networkRules]);
  const forceStrength = useMemo(() => {
    const f = networkRules?.forceStrength;
    if (typeof f !== 'number' || !Number.isFinite(f)) return 1.0;
    return Math.min(2.0, Math.max(0.5, f));
  }, [networkRules]);
  const rowHeight = useMemo(() => Math.round(ROW_HEIGHT_BASE * forceStrength), [forceStrength]);

  const allInsights = useMemo<InsightMinimal[]>(
    () => (insightsState.status === 'ready' ? insightsState.items : []),
    [insightsState],
  );
  const allIdeas = useMemo<IdeaMinimal[]>(
    () => (ideasState.status === 'ready' ? ideasState.items : []),
    [ideasState],
  );

  // ====== グラフ構築 (フィルタ適用後) ======
  const { nodes, links, teamOptions, nicknameOptions, scopeOptions, durationOptions } =
    useMemo(() => {
      // 1. 日付 / テストデータ フィルタ (old2 L206-277)
      let fPosts = posts;
      let fInsights = allInsights;
      let fIdeas = allIdeas;

      if (startDate || endDate) {
        fPosts = fPosts.filter((p) => {
          const d = toDateStr(p.createdAt);
          if (!d) return false;
          if (startDate && d < startDate) return false;
          if (endDate && d > endDate) return false;
          return true;
        });
        const validPostIds = new Set(fPosts.map((p) => p.id));
        fInsights = fInsights.filter((i) => {
          if (i.selectedPostIds.length > 0) {
            return i.selectedPostIds.some((pid) => validPostIds.has(pid));
          }
          const d = toDateStr(i.createdAt);
          if (!d) return false;
          if (startDate && d < startDate) return false;
          if (endDate && d > endDate) return false;
          return true;
        });
        const validInsightIds = new Set(fInsights.map((i) => i.id));
        fIdeas = fIdeas.filter((d) => {
          if (d.insightIds.length > 0) {
            return d.insightIds.some((iid) => validInsightIds.has(iid));
          }
          const ds = toDateStr(d.createdAt);
          if (!ds) return false;
          if (startDate && ds < startDate) return false;
          if (endDate && ds > endDate) return false;
          return true;
        });
      }

      if (excludeTestData) {
        fPosts = fPosts.filter((p) => !isTestText(p.title) && !isTestText(p.authorNickname));
        fInsights = fInsights.filter((i) => !isTestText(i.title) && !isTestText(i.nickname));
        fIdeas = fIdeas.filter((d) => !isTestText(d.title) && !isTestText(d.nickname));
      }

      // 1.5 軸駆動フィルタ (master/dataSettings/M5 由来)。post を絞り、insight/idea は残存 post への
      //     紐付けで連鎖 (紐付け post を持つものは生存 post 必須、持たないものは残す)。
      if (axisFilterActiveCount > 0) {
        fPosts = fPosts.filter((p) => axisFilteredPostIds.has(p.id));
        const survivingPostIds = new Set(fPosts.map((p) => p.id));
        fInsights = fInsights.filter(
          (i) =>
            i.selectedPostIds.length === 0 ||
            i.selectedPostIds.some((pid) => survivingPostIds.has(pid)),
        );
        const survivingInsightIds = new Set(fInsights.map((i) => i.id));
        fIdeas = fIdeas.filter(
          (d) =>
            d.insightIds.length === 0 || d.insightIds.some((iid) => survivingInsightIds.has(iid)),
        );
      }

      // 2. 孤立投稿 非表示 (old2 L271-277)
      if (hideIsolatedPosts) {
        const linkedPostIds = new Set<string>();
        fInsights.forEach((i) => i.selectedPostIds.forEach((pid) => linkedPostIds.add(pid)));
        fPosts = fPosts.filter((p) => linkedPostIds.has(p.id));
      }

      const nodeList: GraphNode[] = [];
      const linkList: GraphLink[] = [];
      const teams = new Set<string>();
      const nicknames = new Set<string>();
      const scopes = new Set<string>();
      const durations = new Set<string>();

      fPosts.forEach((p, idx) => {
        const emotion = p.aiAnalysisEcw?.emotions?.[0] ?? '';
        const emoColor = emotion ? resolveEmotionColor(resolver, emotion) : null;
        const team = readString(p.extraFields, TEAM_KEYS) || (p.sessionId ?? '');
        const nickname = p.authorNickname || '';
        const scope = readString(p.extraFields, SCOPE_KEYS);
        const duration = readString(p.extraFields, DURATION_KEYS);
        if (team) teams.add(team);
        if (nickname) nicknames.add(nickname);
        if (scope) scopes.add(scope);
        if (duration) durations.add(duration);
        nodeList.push({
          id: `post_${p.id}`,
          type: 'post',
          label: p.title || '(無題)',
          description: p.comment || '',
          initialX: COL_LEFT_X,
          initialY: MARGIN_TOP + idx * rowHeight,
          x: COL_LEFT_X,
          y: MARGIN_TOP + idx * rowHeight,
          baseColor: emoColor || POST_BASE_COLOR,
          baseVal: 5,
          team,
          nickname,
          ideaCount: 0,
          degree: 0,
          insightPostLinks: 0,
          insightIdeaLinks: 0,
          imageUrl: p.photoThumbUrl ?? p.photoUrl ?? null,
          postRef: p,
        });
      });

      // 気づき / 処方箋にキャッチ画像が無いときは、紐づく投稿の写真を使う (2026-09-07)。
      // 「ノード内に写真を表示」で何も出ない状態を避ける。処方箋カードの hero と同じ考え方。
      const postPhotoById = new Map<string, string>();
      fPosts.forEach((p) => {
        const url = p.photoThumbUrl ?? p.photoUrl ?? null;
        if (url) postPhotoById.set(p.id, url);
      });
      const firstPostPhoto = (postIds: string[] | undefined): string | null => {
        for (const pid of postIds ?? []) {
          const url = postPhotoById.get(pid);
          if (url) return url;
        }
        return null;
      };
      const insightById = new Map(fInsights.map((i) => [i.id, i]));

      fInsights.forEach((i, idx) => {
        const team = i.team || '';
        const nickname = i.nickname || '';
        if (team) teams.add(team);
        if (nickname) nicknames.add(nickname);
        nodeList.push({
          id: `insight_${i.id}`,
          type: 'insight',
          label: i.title || '(無題の気づき)',
          description: `${i.observations || ''}\n${i.diagnosis || ''}`.trim(),
          initialX: COL_MID_X,
          initialY: MARGIN_TOP + idx * rowHeight,
          x: COL_MID_X,
          y: MARGIN_TOP + idx * rowHeight,
          baseColor: INSIGHT_DEFAULT_COLOR,
          // キャッチ画像 (キービジュアル) を持つ気づきは少し大きく (処方箋の image bonus と同様)。
          baseVal: i.referenceImageBase64 ? 16 : 10,
          team,
          nickname,
          ideaCount: 0,
          degree: 0,
          insightPostLinks: 0,
          insightIdeaLinks: 0,
          // 気づきキービジュアル (dataURL/http URL)。「ノード内に写真を表示」ON で表示。
          // 未指定なら紐づく投稿の写真で代替する。
          imageUrl: i.referenceImageBase64 ?? firstPostPhoto(i.selectedPostIds),
          insightRef: i,
        });
      });

      fIdeas.forEach((d, idx) => {
        const major = d.categoryCode ? (d.categoryCode.match(/^[A-Z]/)?.[0] ?? '') : '';
        const catColor = major ? resolveCategoryMajorColor(resolver, major) : null;
        const team = d.team || '';
        const nickname = d.nickname || '';
        if (team) teams.add(team);
        if (nickname) nicknames.add(nickname);
        nodeList.push({
          id: `idea_${d.id}`,
          type: 'idea',
          label: d.title || '(無題の処方箋)',
          description: d.actionIdea || '',
          initialX: COL_RIGHT_X,
          initialY: MARGIN_TOP + idx * rowHeight,
          x: COL_RIGHT_X,
          y: MARGIN_TOP + idx * rowHeight,
          baseColor: catColor || IDEA_BASE_COLOR,
          baseVal: d.referenceImageBase64 ? 25 : 15,
          team,
          nickname,
          ideaCount: 0,
          degree: 0,
          insightPostLinks: 0,
          insightIdeaLinks: 0,
          // 処方箋も未指定なら写真を代替する。紐づく投稿が無ければ、束ねている気づきの投稿から拾う。
          imageUrl:
            d.referenceImageBase64 ??
            firstPostPhoto(d.selectedPostIds) ??
            firstPostPhoto(
              (d.insightIds ?? []).flatMap((iid) => insightById.get(iid)?.selectedPostIds ?? []),
            ),
          ideaRef: d,
        });
      });

      const nodeById = new Map(nodeList.map((n) => [n.id, n]));

      // 明示リンク
      fInsights.forEach((i) => {
        i.selectedPostIds.forEach((pid) => {
          const target = `post_${pid}`;
          if (nodeById.has(target)) {
            linkList.push({ source: `insight_${i.id}`, target, kind: 'insight-post' });
          }
        });
      });
      fIdeas.forEach((d) => {
        // 結実投稿 集計: idea → insight → post の貢献度カウント (old2 L380-397)
        const uniquePostIds = new Set<string>();
        d.insightIds.forEach((iid) => {
          const target = `insight_${iid}`;
          if (nodeById.has(target)) {
            linkList.push({ source: `idea_${d.id}`, target, kind: 'idea-insight' });
          }
          const ins = fInsights.find((x) => x.id === iid);
          ins?.selectedPostIds.forEach((pid) => uniquePostIds.add(pid));
        });
        uniquePostIds.forEach((pid) => {
          const pNode = nodeById.get(`post_${pid}`);
          if (pNode) pNode.ideaCount += 1;
        });
      });

      // degree 集計 (old2 getCustomVal の links.size 相当)
      linkList.forEach((l) => {
        const s = nodeById.get(l.source as string);
        const t = nodeById.get(l.target as string);
        if (s) {
          s.degree += 1;
          if (s.type === 'insight') {
            if (t?.type === 'post') s.insightPostLinks += 1;
            if (t?.type === 'idea') s.insightIdeaLinks += 1;
          }
        }
        if (t) {
          t.degree += 1;
          if (t.type === 'insight') {
            if (s?.type === 'post') t.insightPostLinks += 1;
            if (s?.type === 'idea') t.insightIdeaLinks += 1;
          }
        }
      });

      // docs/107 Wave6 / old2 L469-479: 類似度リンクは **気づき (insight) 同士**。
      if (showSimilarity && fInsights.length >= 2) {
        type SimPair = { aId: string; bId: string; sim: number };
        const pairs: SimPair[] = [];
        const items = fInsights.map((i) => ({
          id: i.id,
          text: `${i.title || ''} ${i.observations || ''} ${i.diagnosis || ''}`.trim(),
        }));
        for (let i = 0; i < items.length; i += 1) {
          const a = items[i];
          if (!a) continue;
          for (let j = i + 1; j < items.length; j += 1) {
            const b = items[j];
            if (!b) continue;
            const sim = calculateTextSimilarity(a.text, b.text);
            if (sim >= similarityThreshold) pairs.push({ aId: a.id, bId: b.id, sim });
          }
        }
        pairs.sort((x, y) => y.sim - x.sim);
        for (const { aId, bId, sim } of pairs.slice(0, SIMILARITY_MAX_LINKS)) {
          const source = `insight_${aId}`;
          const target = `insight_${bId}`;
          if (nodeById.has(source) && nodeById.has(target)) {
            linkList.push({ source, target, kind: 'similarity', weight: sim });
          }
        }
      }

      return {
        nodes: nodeList,
        links: linkList,
        teamOptions: [...teams].sort(),
        nicknameOptions: [...nicknames].sort(),
        scopeOptions: [...scopes],
        durationOptions: [...durations].sort(),
      };
    }, [
      posts,
      allInsights,
      allIdeas,
      showSimilarity,
      similarityThreshold,
      rowHeight,
      resolver,
      startDate,
      endDate,
      excludeTestData,
      hideIsolatedPosts,
      axisFilteredPostIds,
      axisFilterActiveCount,
    ]);

  const svgWidth = COL_RIGHT_X + COL_WIDTH;

  // 円状(放射状)レイアウト用の正方キャンバス。ノード数に応じて半径を拡大し、forceCenter で
  // 中央に集約 → 全体が収まる正方 viewBox に収める (列の縦伸びによる「集中が見にくい」を解消)。
  const circularSize = useMemo(() => {
    const r = Math.max(180, Math.sqrt(Math.max(1, nodes.length)) * 60);
    return Math.round(r * 2 + 120);
  }, [nodes.length]);

  // ====== ノード表示色 (color mode 別) ======
  const nodeDisplayColor = useCallback(
    (n: GraphNode): string => {
      if (n.type === 'insight') {
        if (insightColorMode === 'emotion' && n.insightRef?.emotionTag) {
          return resolveEmotionColor(resolver, n.insightRef.emotionTag) || n.baseColor;
        }
        if (insightColorMode === 'sentiment') {
          return sentimentRgb(n.insightRef?.sentiment ?? 0);
        }
        return n.baseColor;
      }
      if (n.type !== 'post' || !n.postRef) return n.baseColor;
      const p = n.postRef;
      switch (postColorMode) {
        case 'quadrant':
          return getQuadrantColor(happyOf(p), healthOf(p));
        case 'categoryMajor': {
          const code = p.aiAnalysisEcw?.categoryCodes?.[0] ?? '';
          const major = code.includes('-') ? code.split('-')[0]! : code.charAt(0);
          return (major ? resolveCategoryMajorColor(resolver, major) : null) || '#888';
        }
        case 'team':
          return stringToColor(n.team || '不明');
        case 'nickname':
          return stringToColor(n.nickname || '不明');
        case 'scope':
          return stringToColor(readString(p.extraFields, SCOPE_KEYS) || '不明');
        case 'duration':
          return stringToColor(readString(p.extraFields, DURATION_KEYS) || '不明');
        case 'emotion':
          return n.baseColor;
        default:
          // 動的軸キー (matrixAxisModel) のとき bucket 色で塗る。'emotion' や未知値は baseColor。
          return axisFilter.colorForPost(p, postColorMode) ?? n.baseColor;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [postColorMode, insightColorMode, resolver, axisFilter.filterGroupsByKey],
  );

  // ====== ノードサイズ (次数 / 結実) ======
  const nodeRadius = useCallback(
    (n: GraphNode): number => {
      let mult = 1;
      if (useDegreeSize) {
        if (n.type === 'insight') {
          const v = n.insightPostLinks * degreeMultiplier + n.insightIdeaLinks * degreeMultiplier;
          mult = Math.max(1, v);
        } else {
          mult = Math.max(1, n.degree * degreeMultiplier);
        }
      }
      let val = n.baseVal * (mult * mult);
      if (n.type === 'post' && highlightIdeaPosts && n.ideaCount > 0) {
        val += n.ideaCount * 30;
      }
      // px 半径に変換 (old2: sqrt(val)*1.5 を v2 SVG 縮尺に合わせて clamp)。
      const r = Math.sqrt(Math.max(1, val)) * 1.5;
      return Math.min(48, Math.max(6, r));
    },
    [useDegreeSize, degreeMultiplier, highlightIdeaPosts],
  );

  // ====== 整列(3列) の配置 ======
  // 列ごとに上から積む。「リンク数でノードの大きさを変える」で半径が行間より大きくなったときは、
  // 隣り合うノードの半径ぶんだけ行間を広げて図形が重ならないようにする (2026-09-05)。
  // 半径が小さいうちは従来の等間隔 (rowHeight) と同じ結果になる。
  const columnLayout = useMemo(() => {
    const pos = new Map<string, { x: number; y: number }>();
    const cursor: Record<NodeType, { y: number; r: number } | null> = {
      post: null,
      insight: null,
      idea: null,
    };
    let maxY = MARGIN_TOP;
    let maxBottom = MARGIN_TOP;
    nodes.forEach((n) => {
      const r = verticalHalfExtent(n.type, nodeRadius(n));
      const prev = cursor[n.type];
      const y = prev
        ? prev.y + Math.max(rowHeight, prev.r + r + COLUMN_NODE_GAP)
        : Math.max(MARGIN_TOP, COLUMN_HEADER_BOTTOM + r);
      cursor[n.type] = { y, r };
      pos.set(n.id, { x: n.initialX, y });
      maxY = Math.max(maxY, y);
      maxBottom = Math.max(maxBottom, y + r);
    });
    return { pos, height: Math.max(maxY + rowHeight, maxBottom + COLUMN_NODE_GAP) + 32 };
  }, [nodes, nodeRadius, rowHeight]);
  const columnPosOf = useCallback(
    (n: GraphNode) => columnLayout.pos.get(n.id) ?? { x: n.initialX, y: n.initialY },
    [columnLayout],
  );

  // 配置基準サイズ: 円状(放射状)時は正方、整列(3列)時は列レイアウト寸法 (行間の広がりを含む)。
  const layoutW = simulationEnabled ? circularSize : svgWidth;
  const layoutH = simulationEnabled ? circularSize : columnLayout.height;

  // ====== focus / 強調マスク ======
  const focusedSet = useMemo(() => {
    if (!focusId) return null;
    const set = new Set<string>([focusId]);
    links.forEach((l) => {
      if (l.kind !== 'insight-post' && l.kind !== 'idea-insight') return;
      const s = typeof l.source === 'string' ? l.source : l.source.id;
      const t = typeof l.target === 'string' ? l.target : l.target.id;
      if (s === focusId) set.add(t);
      if (t === focusId) set.add(s);
    });
    return set;
  }, [focusId, links]);

  const isNodeMasked = useCallback(
    (n: GraphNode): boolean => {
      if (highlightTeam && n.team !== highlightTeam) return true;
      if (highlightNickname && n.nickname !== highlightNickname) return true;
      if (highlightIdeaPosts && n.type === 'post' && n.ideaCount === 0) return true;
      return false;
    },
    [highlightTeam, highlightNickname, highlightIdeaPosts],
  );

  const isNodeHighlighted = useCallback(
    (n: GraphNode): boolean => {
      if (isNodeMasked(n)) return false;
      if (focusedSet) return focusedSet.has(n.id);
      return true;
    },
    [isNodeMasked, focusedSet],
  );

  const nodeMap = useMemo(() => {
    const m = new Map<string, GraphNode>();
    nodes.forEach((n) => m.set(n.id, n));
    return m;
  }, [nodes]);

  const selectedNode = selectedId ? (nodeMap.get(selectedId) ?? null) : null;

  // ====== d3-force simulation (Sprint BD base 維持 + gravity 追加) ======
  const simNodesRef = useRef<GraphNode[]>([]);
  const simLinksRef = useRef<GraphLink[]>([]);
  const simulationRef = useRef<Simulation<GraphNode, GraphLink> | null>(null);
  const nodeElsRef = useRef<Map<string, SVGGElement>>(new Map());
  const linkElsRef = useRef<Map<number, SVGLineElement | SVGPathElement>>(new Map());

  const applyTick = useCallback(() => {
    const ns = simNodesRef.current;
    const ls = simLinksRef.current;
    ns.forEach((n) => {
      const el = nodeElsRef.current.get(n.id);
      if (!el) return;
      el.setAttribute('transform', `translate(${n.x ?? n.initialX},${n.y ?? n.initialY})`);
    });
    ls.forEach((l, idx) => {
      const el = linkElsRef.current.get(idx);
      if (!el) return;
      const s = typeof l.source === 'object' ? l.source : null;
      const t = typeof l.target === 'object' ? l.target : null;
      if (!s || !t) return;
      const sx = s.x ?? 0;
      const sy = s.y ?? 0;
      const tx = t.x ?? 0;
      const ty = t.y ?? 0;
      if (l.kind === 'similarity') {
        const dy = Math.abs(ty - sy);
        const bulge = Math.min(80, dy * 0.5 + 20);
        (el as SVGPathElement).setAttribute(
          'd',
          `M ${sx} ${sy} Q ${sx - bulge} ${(sy + ty) / 2} ${tx} ${ty}`,
        );
      } else {
        const line = el as SVGLineElement;
        line.setAttribute('x1', String(sx));
        line.setAttribute('y1', String(sy));
        line.setAttribute('x2', String(tx));
        line.setAttribute('y2', String(ty));
      }
    });
  }, []);

  // 直前の円状配置の持ち越し。フィルタや力の強さを変えたときに、それまでの位置から続けて計算する
  // (毎回円周に置き直さない)。整列(3列) を経由したら null に戻し、次に円状へ切り替えたときは改めて
  // 円周から warm-start する (列の並びを引きずらないため)。
  const carryRef = useRef<{ nodes: GraphNode[]; cx: number; cy: number } | null>(null);
  // 衝突半径は表示半径に追従させる。effect の依存に nodeRadius を入れると倍率スライダーのたびに
  // simulation が作り直されるので ref 経由で読む (更新は下の専用 effect)。
  const nodeRadiusRef = useRef(nodeRadius);
  useEffect(() => {
    nodeRadiusRef.current = nodeRadius;
  }, [nodeRadius]);
  useEffect(() => {
    frozenRef.current = frozen;
  }, [frozen]);

  useEffect(() => {
    const cx = layoutW / 2;
    const cy = layoutH / 2;
    const carry = simulationEnabled ? carryRef.current : null;
    const carried = new Map<string, GraphNode>();
    carry?.nodes.forEach((n) => {
      if (n.x != null && n.y != null) carried.set(n.id, n);
    });
    // 正方キャンバスの大きさが変わっても相対位置が保たれるよう、中心のずれ分だけ平行移動する。
    const dcx = carry ? cx - carry.cx : 0;
    const dcy = carry ? cy - carry.cy : 0;
    simNodesRef.current = nodes.map((n) => {
      const copy: GraphNode = { ...n };
      const prev = carried.get(n.id);
      if (prev) {
        copy.x = (prev.x as number) + dcx;
        copy.y = (prev.y as number) + dcy;
        copy.vx = 0;
        copy.vy = 0;
      }
      return copy;
    });
    simLinksRef.current = links.map((l) => ({ ...l }));

    if (simulationRef.current) {
      simulationRef.current.stop();
      simulationRef.current = null;
    }
    if (!simulationEnabled || simNodesRef.current.length === 0) {
      carryRef.current = null;
      return;
    }

    // 円状(放射状) warm-start: 列バイアスを消すためノードを中心まわりの円周に初期配置してから
    // forceManyBody(反発) + forceCenter(中心引力) で settle させる → 関連が寄り集まり「集中」が見える。
    // 持ち越し位置のあるノードはそのまま、新しく現れたノードだけ円周に置く。
    {
      const fresh = simNodesRef.current.filter((n) => !carried.has(n.id));
      const total = simNodesRef.current.length;
      const warmR = Math.max(80, Math.sqrt(total) * 45);
      fresh.forEach((node, i) => {
        const ang = (i / Math.max(1, fresh.length)) * Math.PI * 2;
        node.x = cx + Math.cos(ang) * warmR;
        node.y = cy + Math.sin(ang) * warmR;
      });
    }

    const sim = forceSimulation<GraphNode, GraphLink>(simNodesRef.current)
      .force(
        'link',
        forceLink<GraphNode, GraphLink>(simLinksRef.current)
          .id((d) => d.id)
          .distance(Math.max(20, 80 / forceStrength))
          .strength(0.5),
      )
      .force('charge', forceManyBody().strength(-200 * forceStrength))
      .force('center', forceCenter(layoutW / 2, layoutH / 2))
      // 衝突半径 = 表示半径 + 余白。リンク数で大きくしたノードどうしが重ならない (2026-09-05)。
      .force(
        'collide',
        forceCollide<GraphNode>((n) => collideRadiusOf(n.type, nodeRadiusRef.current(n))),
      )
      .alpha(1)
      .alphaDecay(0.03)
      .on('tick', applyTick);

    // 引力クラスタリング (old2 gravity*、forceX/forceY、同属性を放射状に引き寄せる)
    if (gravityTeam > 0 && teamOptions.length > 0) {
      const r = 400;
      sim
        .force(
          'gx',
          forceX<GraphNode>((n) => {
            const i = teamOptions.indexOf(n.team);
            return i >= 0
              ? Math.cos((i / teamOptions.length) * Math.PI * 2) * r + layoutW / 2
              : layoutW / 2;
          }).strength((n) => (n.team ? gravityTeam * 0.05 : 0)),
        )
        .force(
          'gy',
          forceY<GraphNode>((n) => {
            const i = teamOptions.indexOf(n.team);
            return i >= 0
              ? Math.sin((i / teamOptions.length) * Math.PI * 2) * r + layoutH / 2
              : layoutH / 2;
          }).strength((n) => (n.team ? gravityTeam * 0.05 : 0)),
        );
    } else if (gravityNickname > 0 && nicknameOptions.length > 0) {
      const r = 400;
      sim
        .force(
          'gx',
          forceX<GraphNode>((n) => {
            const i = nicknameOptions.indexOf(n.nickname);
            return i >= 0
              ? Math.cos((i / nicknameOptions.length) * Math.PI * 2) * r + layoutW / 2
              : layoutW / 2;
          }).strength((n) => (n.nickname ? gravityNickname * 0.05 : 0)),
        )
        .force(
          'gy',
          forceY<GraphNode>((n) => {
            const i = nicknameOptions.indexOf(n.nickname);
            return i >= 0
              ? Math.sin((i / nicknameOptions.length) * Math.PI * 2) * r + layoutH / 2
              : layoutH / 2;
          }).strength((n) => (n.nickname ? gravityNickname * 0.05 : 0)),
        );
    }

    simulationRef.current = sim;
    carryRef.current = { nodes: simNodesRef.current, cx, cy };
    applyTick(); // warm-start 位置を即時描画 (初期フラッシュ防止)
    if (frozenRef.current) {
      // 固定中に条件が変わった場合: 持ち越した位置のまま動かさない
      sim.alpha(0).stop();
    }
    return () => {
      sim.stop();
      if (simulationRef.current === sim) simulationRef.current = null;
    };
  }, [
    nodes,
    links,
    simulationEnabled,
    forceStrength,
    layoutW,
    layoutH,
    gravityTeam,
    gravityNickname,
    teamOptions,
    nicknameOptions,
    applyTick,
  ]);

  // 「リンク数でノードの大きさを変える」等で半径が変わったら衝突半径も追従させ、重なりをほどくため
  // 少しだけ再加熱する。固定中は動かさない。起動直後 (alpha が高い) は下げない。
  useEffect(() => {
    const sim = simulationRef.current;
    if (!sim) return;
    const collide = sim.force('collide') as ForceCollide<GraphNode> | undefined;
    collide?.radius((n) => collideRadiusOf(n.type, nodeRadius(n)));
    if (!frozenRef.current && sim.alpha() < 0.3) sim.alpha(0.3).restart();
  }, [nodeRadius]);

  /**
   * 「配置を固定」: その時点の位置で力学計算を止める (置き直さない)。外したら同じ位置から再開する。
   * simulation effect を再実行させないよう、state と ref を更新して simulation を直接止める/動かす。
   */
  const toggleFrozen = (next: boolean) => {
    frozenRef.current = next;
    setFrozen(next);
    const sim = simulationRef.current;
    if (!sim) return;
    if (next) sim.alphaTarget(0).alpha(0).stop();
    else sim.alphaTarget(0).alpha(0.3).restart();
  };

  /** レイアウト切替。座標系 (viewBox) が変わるので視点は初期化し、整列(3列) では固定を解除する。 */
  const switchLayout = (toSimulation: boolean) => {
    if (toSimulation === simulationEnabled) return;
    setSimulationEnabled(toSimulation);
    if (!toSimulation) {
      frozenRef.current = false;
      setFrozen(false);
    }
    applyView({ k: 1, x: 0, y: 0 });
  };

  // ====== ノードクリック → 中心ズーム + 詳細 (old2 handleNodeClick) ======
  const centerOnNode = useCallback(
    (n: GraphNode) => {
      const live = simNodesRef.current.find((sn) => sn.id === n.id);
      const col = columnPosOf(n);
      const x = (simulationEnabled ? live?.x : undefined) ?? col.x;
      const y = (simulationEnabled ? live?.y : undefined) ?? col.y;
      const k = 2.2;
      // 画面中心 = svg viewport の中央 (viewBox 座標系) に node を持ってくる。
      applyView({ k, x: layoutW / 2 - x * k, y: layoutH / 2 - y * k });
    },
    [simulationEnabled, layoutW, layoutH, columnPosOf, applyView],
  );

  const handleNodeClick = (n: GraphNode) => {
    if (n.type === 'post' && n.postRef && onSelectPost) {
      onSelectPost(n.postRef);
      return;
    }
    // 気づき / 処方箋も投稿と同じように詳細を開く (2026-09-07)。あわせて選択状態にして
    // 図の上でも関連が分かるようにする (投稿は図に残らないので選択しない)。
    if (n.type === 'insight' && n.insightRef && onSelectInsight) {
      setSelectedId(n.id);
      setFocusId(n.id);
      onSelectInsight(n.insightRef);
      return;
    }
    if (n.type === 'idea' && n.ideaRef && onSelectIdea) {
      setSelectedId(n.id);
      setFocusId(n.id);
      onSelectIdea(n.ideaRef);
      return;
    }
    if (selectedId === n.id) {
      setSelectedId(null);
      setFocusId(null);
    } else {
      setSelectedId(n.id);
      setFocusId(n.id);
      centerOnNode(n);
    }
  };

  // ====== 全体表示 (fit) — ノード bbox に合わせる ======
  const fitToView = useCallback(() => {
    applyView({ k: 1, x: 0, y: 0 });
  }, [applyView]);

  // ====== ズーム (wheel) / パン (背景ドラッグ) ======
  /**
   * ホイールズーム (2026-09-05 修正)。
   *
   * **setState の更新関数の中でイベントを読んではいけない。** 合成イベントの `currentTarget` は
   * 伝播中しか有効で、ハンドラを抜けると null になる。React は更新をまとめてレンダー時に適用するため、
   * 直前の更新が残っていると更新関数はハンドラを抜けた後に実行され
   * `Cannot read properties of null (reading 'getBoundingClientRect')` で落ちる
   * (キューが空なら dispatch 時に即時実行されるので「最初は動くのに操作すると落ちる」挙動になっていた)。
   *
   * 位置計算に要る値はハンドラ内で数値として取り出し、更新関数には純粋計算 (computeWheelZoom) だけを渡す。
   */
  const handleWheel = (e: React.WheelEvent<SVGSVGElement>) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const { clientX, clientY, deltaY } = e;
    applyView(
      computeWheelZoom(viewRef.current, { rect, clientX, clientY, deltaY, layoutW, layoutH }),
    );
  };

  // ====== パン (背景ドラッグ) / ピンチ (2 本指の拡大縮小、2026-09-05) ======
  // svg 上のポインタ (指) をまとめて追跡し、1 本ならパン、2 本になった時点でピンチに切り替える。
  // ノードを掴んでいる指も数えるので、片方の指がノードに掛かっていてもピンチできる。
  // svg には `touch-none` を付けてあるので、ブラウザ側のスクロール/ページ拡大に取られず
  // すべての指が pointer イベントとして届く。
  const gestureRef = useRef<{
    pointers: Map<number, ClientPoint>;
    pan: PanStart | null;
    pinch: PinchStart | null;
  }>({ pointers: new Map(), pan: null, pinch: null });
  const dragStateRef = useRef<{ nodeId: string; pointerId: number } | null>(null);

  const cancelNodeDrag = () => {
    const st = dragStateRef.current;
    if (!st) return;
    const live = simNodesRef.current.find((sn) => sn.id === st.nodeId);
    if (live) {
      live.fx = null;
      live.fy = null;
    }
    if (!frozenRef.current) simulationRef.current?.alphaTarget(0);
    dragStateRef.current = null;
  };

  const isNodeTarget = (t: EventTarget | null): boolean =>
    t instanceof Element && t.closest('[data-network-node]') !== null;

  const gesturePointerDown = (e: React.PointerEvent<Element>, opts: { allowPan: boolean }) => {
    const g = gestureRef.current;
    const pt: ClientPoint = { clientX: e.clientX, clientY: e.clientY };
    g.pointers.set(e.pointerId, pt);
    if (g.pointers.size >= 2) {
      const ids = [...g.pointers.keys()];
      const a = ids[ids.length - 2]!;
      const b = ids[ids.length - 1]!;
      g.pan = null;
      g.pinch = pinchStartFrom([a, b], g.pointers.get(a)!, g.pointers.get(b)!, viewRef.current);
      cancelNodeDrag(); // ノードを掴んでいた指もピンチに回す
    } else if (opts.allowPan) {
      g.pan = { pointerId: e.pointerId, start: pt, view: viewRef.current };
    }
  };

  const handleSvgPointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
    // ノード上から始まったポインタは捕捉しない (svg に捕捉すると click がノードに届かなくなる)。
    if (!isNodeTarget(e.target)) e.currentTarget.setPointerCapture?.(e.pointerId);
    gesturePointerDown(e, { allowPan: true });
  };
  const handleSvgPointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const g = gestureRef.current;
    if (!g.pointers.has(e.pointerId)) return;
    const pt: ClientPoint = { clientX: e.clientX, clientY: e.clientY };
    g.pointers.set(e.pointerId, pt);
    const rect = e.currentTarget.getBoundingClientRect();
    if (g.pinch) {
      const a = g.pointers.get(g.pinch.pointerIds[0]);
      const b = g.pointers.get(g.pinch.pointerIds[1]);
      if (a && b) applyView(computePinchZoom(g.pinch, a, b, rect, layoutW, layoutH));
      return;
    }
    if (g.pan && g.pan.pointerId === e.pointerId) {
      applyView(computePan(g.pan, pt, rect, layoutW, layoutH));
    }
  };
  const handleSvgPointerUp = (e: React.PointerEvent<SVGSVGElement>) => {
    const g = gestureRef.current;
    g.pointers.delete(e.pointerId);
    if (g.pinch && g.pinch.pointerIds.includes(e.pointerId)) {
      g.pinch = null;
      // 1 本残っていればその指からパンを続ける (視点が飛ばないよう現在の表示位置を起点にする)。
      const rest = [...g.pointers.entries()][0];
      g.pan = rest ? { pointerId: rest[0], start: rest[1], view: viewRef.current } : null;
      return;
    }
    if (g.pan?.pointerId === e.pointerId) g.pan = null;
  };
  /** 捕捉していないポインタ (ノード上から始めたもの) が svg の外へ出たら追跡をやめる。 */
  const handleSvgPointerLeave = (e: React.PointerEvent<SVGSVGElement>) => {
    const g = gestureRef.current;
    if (!g.pointers.has(e.pointerId)) return;
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) return;
    handleSvgPointerUp(e);
  };

  // ====== ノードドラッグ (simulation ON 時) ======
  const handleNodePointerDown = (e: React.PointerEvent<SVGGElement>, n: GraphNode) => {
    if (!simulationEnabled) return;
    e.stopPropagation();
    const sim = simulationRef.current;
    if (!sim) return;
    const live = simNodesRef.current.find((sn) => sn.id === n.id);
    if (!live) return;
    gesturePointerDown(e, { allowPan: false });
    if (gestureRef.current.pinch) return; // 2 本目の指 → ピンチに回す (掴まない)
    dragStateRef.current = { nodeId: n.id, pointerId: e.pointerId };
    live.fx = live.x;
    live.fy = live.y;
    // 固定中は他のノードを動かさず、掴んだノードだけ動かす (再加熱しない)。
    if (!frozenRef.current) sim.alphaTarget(0.3).restart();
    (e.target as Element).setPointerCapture?.(e.pointerId);
  };
  const handleNodePointerMove = (e: React.PointerEvent<SVGGElement>) => {
    const st = dragStateRef.current;
    if (!st || st.pointerId !== e.pointerId) return;
    if (gestureRef.current.pinch) return;
    const live = simNodesRef.current.find((sn) => sn.id === st.nodeId);
    if (!live) return;
    const svg = e.currentTarget.ownerSVGElement;
    if (!svg) return;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return;
    // viewport → world: g 内 transform(view) を逆適用
    const local = pt.matrixTransform(ctm.inverse());
    const v = viewRef.current;
    live.fx = (local.x - v.x) / v.k;
    live.fy = (local.y - v.y) / v.k;
    if (frozenRef.current) {
      // simulation が止まっているので、掴んだノードだけ自前で描き直す
      live.x = live.fx;
      live.y = live.fy;
      applyTick();
    }
  };
  const handleNodePointerUp = (e: React.PointerEvent<SVGGElement>) => {
    const st = dragStateRef.current;
    if (!st || st.pointerId !== e.pointerId) return;
    const live = simNodesRef.current.find((sn) => sn.id === st.nodeId);
    if (live) {
      live.fx = null;
      live.fy = null;
    }
    if (!frozenRef.current) simulationRef.current?.alphaTarget(0);
    dragStateRef.current = null;
  };

  const isLoading = insightsState.status === 'loading' || ideasState.status === 'loading';

  const attachNodeRef = (id: string) => (el: SVGGElement | null) => {
    if (el) nodeElsRef.current.set(id, el);
    else nodeElsRef.current.delete(id);
  };
  const attachLinkRef = (idx: number) => (el: SVGLineElement | SVGPathElement | null) => {
    if (el) linkElsRef.current.set(idx, el);
    else linkElsRef.current.delete(idx);
  };

  const sliderCls = 'w-full cursor-pointer accent-primary-600';
  const selectCls =
    'w-full px-2 py-1 text-xs bg-white border border-slate-300 rounded text-slate-700';
  const panelSectionCls = 'mb-3 p-2.5 bg-slate-50 rounded border border-slate-200';

  return (
    <div className="flex flex-col h-full bg-white relative">
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 shrink-0 flex flex-wrap items-center gap-4 text-xs">
        <h2 className="font-semibold text-slate-700">ネットワーク</h2>
        <span className="text-slate-600 flex items-center gap-1">
          <span
            className="inline-block w-3 h-3"
            style={{ backgroundColor: POST_BASE_COLOR }}
            aria-hidden
          />
          投稿 {posts.length}
        </span>
        <span className="text-slate-600 flex items-center gap-1">
          <span
            className="inline-block w-3 h-3 rotate-45"
            style={{ backgroundColor: INSIGHT_DEFAULT_COLOR }}
            aria-hidden
          />
          気づき {allInsights.length}
        </span>
        <span className="text-slate-600 flex items-center gap-1">
          <span
            className="inline-block w-3 h-3 rounded-full"
            style={{ backgroundColor: IDEA_BASE_COLOR }}
            aria-hidden
          />
          処方箋 {allIdeas.length}
        </span>
        <label className="text-slate-600 flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={showSimilarity}
            onChange={(e) => setShowSimilarity(e.target.checked)}
          />
          類似度 link (気づき同士)
        </label>
        {/* レイアウト切替: 整列(3列) ⇔ 円状(放射状フォース)。数が多いと列が縦に伸びて集中が見にくく
            なるため、関連が寄り集まる放射状配置に切り替えられる (old2 force-graph 踏襲)。 */}
        <div className="flex items-center gap-1.5" role="group" aria-label="レイアウト切替">
          <span className="text-slate-600">レイアウト</span>
          <div className="inline-flex rounded-md border border-slate-300 overflow-hidden">
            <button
              type="button"
              aria-pressed={!simulationEnabled}
              onClick={() => switchLayout(false)}
              className={`px-2.5 py-1 ${
                !simulationEnabled
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-100'
              }`}
            >
              整列（3列）
            </button>
            <button
              type="button"
              aria-pressed={simulationEnabled}
              onClick={() => switchLayout(true)}
              className={`px-2.5 py-1 border-l border-slate-300 ${
                simulationEnabled
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-100'
              }`}
            >
              円状（放射状）
            </button>
          </div>
        </div>
        {simulationEnabled && (
          <label className="text-slate-600 flex items-center gap-1.5">
            <input
              type="checkbox"
              checked={frozen}
              onChange={(e) => toggleFrozen(e.target.checked)}
            />
            配置を固定
          </label>
        )}
        <button
          type="button"
          onClick={() => setShowManual(true)}
          className="text-[11px] text-slate-500 hover:text-primary-700"
          aria-label="使い方マニュアルを開く"
        >
          ❓ 使い方
        </button>
        {(focusId || selectedId) && (
          <button
            type="button"
            onClick={() => {
              setFocusId(null);
              setSelectedId(null);
            }}
            className="text-[11px] text-primary-700 hover:underline ml-auto"
          >
            選択解除
          </button>
        )}
      </div>

      <div className="flex-1 overflow-hidden p-4 relative">
        {isLoading && <p className="text-sm text-slate-500">読込中…</p>}
        {!isLoading && nodes.length === 0 && (
          <p className="text-sm text-slate-500">
            ノードがありません。投稿 / 気づき / 処方箋を作成すると関係が表示されます。
            {hideIsolatedPosts && '（孤立投稿は既定で非表示です）'}
          </p>
        )}
        {!isLoading && nodes.length > 0 && (
          <svg
            width="100%"
            // 常にグラフ領域いっぱい (下部まで) に広げる。列の高さは viewBox(layoutH) で表現し、
            // preserveAspectRatio(meet) で全体がコンテナに収まる。
            height="100%"
            viewBox={`0 0 ${layoutW} ${layoutH}`}
            role="img"
            aria-label="投稿・気づき・処方箋のネットワーク図"
            className="bg-white border border-slate-200 rounded touch-none"
            onWheel={handleWheel}
            onPointerDown={handleSvgPointerDown}
            onPointerMove={handleSvgPointerMove}
            onPointerUp={handleSvgPointerUp}
            onPointerCancel={handleSvgPointerUp}
            onPointerLeave={handleSvgPointerLeave}
          >
            {/* 背景 (選択解除。パン / ピンチは svg 側で扱う) */}
            <rect
              x={0}
              y={0}
              width={layoutW}
              height={layoutH}
              fill="transparent"
              onPointerDown={() => {
                setSelectedId(null);
                setFocusId(null);
              }}
              className="cursor-move"
            />
            {/* key でレイアウト切替時に描画し直す: 円状では simulation が transform / 線の端点を DOM に直接
                書き込むため、React 側の属性値が同じままだと整列(3列) に戻しても古い位置が残る (2026-09-05)。 */}
            <g
              key={simulationEnabled ? 'force' : 'columns'}
              transform={`translate(${view.x},${view.y}) scale(${view.k})`}
            >
              {!simulationEnabled && (
                <>
                  <text x={COL_LEFT_X} y={28} fontSize={12} fill="#475569" fontWeight={600}>
                    投稿
                  </text>
                  <text x={COL_MID_X} y={28} fontSize={12} fill="#475569" fontWeight={600}>
                    気づきカード
                  </text>
                  <text x={COL_RIGHT_X} y={28} fontSize={12} fill="#475569" fontWeight={600}>
                    処方箋カード
                  </text>
                </>
              )}

              {links.map((l, idx) => {
                const sId = typeof l.source === 'string' ? l.source : l.source.id;
                const tId = typeof l.target === 'string' ? l.target : l.target.id;
                const s = nodeMap.get(sId);
                const t = nodeMap.get(tId);
                if (!s || !t) return null;
                const dimmed =
                  (focusedSet !== null && !(focusedSet.has(s.id) && focusedSet.has(t.id))) ||
                  isNodeMasked(s) ||
                  isNodeMasked(t);
                // 円状では simulation が端点を書き換えるので props は初期値のまま固定しておく
                // (props が変わると React が古い位置で上書きしてしまうため)。整列(3列) は列位置。
                const sp = simulationEnabled ? { x: s.initialX, y: s.initialY } : columnPosOf(s);
                const tp = simulationEnabled ? { x: t.initialX, y: t.initialY } : columnPosOf(t);

                if (l.kind === 'similarity') {
                  const weight = l.weight ?? 0;
                  const dy = Math.abs(tp.y - sp.y);
                  const bulge = Math.min(80, dy * 0.5 + 20);
                  return (
                    <path
                      key={`l-${idx}`}
                      ref={attachLinkRef(idx)}
                      d={`M ${sp.x} ${sp.y} Q ${sp.x - bulge} ${(sp.y + tp.y) / 2} ${tp.x} ${tp.y}`}
                      stroke={dimmed ? LINK_DIMMED_COLOR : LINK_SIMILARITY_COLOR}
                      strokeWidth={dimmed ? 0.75 : 1}
                      strokeDasharray="3 3"
                      fill="none"
                      opacity={dimmed ? 0.2 : Math.min(0.7, 0.25 + weight * 0.8)}
                    >
                      <title>類似度 {(weight * 100).toFixed(0)}%</title>
                    </path>
                  );
                }
                const color = dimmed
                  ? LINK_DIMMED_COLOR
                  : l.kind === 'idea-insight'
                    ? LINK_IDEA_INSIGHT_COLOR
                    : LINK_INSIGHT_POST_COLOR;
                return (
                  <line
                    key={`l-${idx}`}
                    ref={attachLinkRef(idx)}
                    x1={sp.x}
                    y1={sp.y}
                    x2={tp.x}
                    y2={tp.y}
                    stroke={color}
                    strokeWidth={dimmed ? 1 : 1.5}
                    opacity={dimmed ? 0.3 : 0.85}
                  />
                );
              })}

              {nodes.map((n) => {
                const highlighted = isNodeHighlighted(n);
                const isSel = selectedId === n.id;
                const r = nodeRadius(n) + (isSel ? NODE_RADIUS_FOCUS_BONUS : 0);
                const fill = highlighted ? nodeDisplayColor(n) : DIMMED_NODE_FILL;
                const stroke = isSel ? '#1e293b' : '#fff';
                const clipId = `clip-${n.id}`;
                const showImg = showImage && n.imageUrl && highlighted;
                const p = simulationEnabled ? { x: n.initialX, y: n.initialY } : columnPosOf(n);
                return (
                  <g
                    key={n.id}
                    ref={attachNodeRef(n.id)}
                    data-network-node=""
                    transform={`translate(${p.x},${p.y})`}
                    className={simulationEnabled ? 'cursor-grab' : 'cursor-pointer'}
                    onClick={() => handleNodeClick(n)}
                    onPointerDown={(e) => handleNodePointerDown(e, n)}
                    onPointerMove={handleNodePointerMove}
                    onPointerUp={handleNodePointerUp}
                    onPointerCancel={handleNodePointerUp}
                    role="button"
                    tabIndex={0}
                    aria-label={`${TYPE_LABEL[n.type]}: ${n.label}`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleNodeClick(n);
                      }
                    }}
                    opacity={highlighted ? 1 : 0.4}
                  >
                    {showImg && (
                      <clipPath id={clipId}>
                        <NodeShape type={n.type} r={r} />
                      </clipPath>
                    )}
                    {showImg ? (
                      <>
                        <image
                          href={n.imageUrl ?? undefined}
                          x={-r}
                          y={-r}
                          width={r * 2}
                          height={r * 2}
                          preserveAspectRatio="xMidYMid slice"
                          clipPath={`url(#${clipId})`}
                        />
                        <NodeShape
                          type={n.type}
                          r={r}
                          fill="none"
                          stroke={fill}
                          strokeWidth={isSel ? 3 : 2}
                        />
                      </>
                    ) : (
                      <NodeShape
                        type={n.type}
                        r={r}
                        fill={fill}
                        stroke={stroke}
                        strokeWidth={isSel ? 2 : 1.5}
                      />
                    )}
                    {(isSel || highlighted) && (
                      <text
                        x={0}
                        y={r + 11}
                        fontSize={10}
                        textAnchor="middle"
                        fill="#1e293b"
                        style={{ pointerEvents: 'none', userSelect: 'none' }}
                      >
                        {n.label.length > 14 ? n.label.slice(0, 14) + '…' : n.label}
                      </text>
                    )}
                    <title>{n.label}</title>
                  </g>
                );
              })}
            </g>
          </svg>
        )}

        {/* 全体表示 (fit) ボタン */}
        {!isLoading && nodes.length > 0 && (
          <button
            type="button"
            onClick={fitToView}
            className="absolute left-1/2 -translate-x-1/2 bottom-4 px-5 py-2 rounded-full bg-primary-600 text-white text-xs font-semibold shadow-lg hover:bg-primary-700"
          >
            🔍 全体表示
          </button>
        )}

        {/* サイドコントロールパネル (折りたたみ ☰)。
            ラッパーを top-2〜bottom-2 で高さ確定 → 内側 max-h-full overflow-y-auto が効き、
            スマホ等で画面に収まらない場合もスクロールで最下部まで表示できる。
            空き領域はグラフ操作を妨げないよう pointer-events-none、ボタン/パネルのみ有効化。 */}
        <div className="absolute top-2 right-2 bottom-2 z-10 flex flex-col items-end pointer-events-none">
          {!panelOpen ? (
            <button
              type="button"
              onClick={() => setPanelOpen(true)}
              className="pointer-events-auto w-10 h-10 flex items-center justify-center bg-white border border-slate-300 rounded-lg shadow text-lg text-slate-600"
              aria-label="コントロールパネルを開く"
            >
              ☰
            </button>
          ) : (
            <div className="pointer-events-auto w-[280px] max-w-[calc(100vw-1rem)] max-h-full overflow-y-auto bg-white/95 backdrop-blur border border-slate-300 rounded-lg shadow-lg p-3 text-xs">
              {/* スクロールしても閉じられるよう header は sticky 固定 */}
              <div className="sticky top-0 z-10 -mx-3 px-3 -mt-3 pt-3 pb-2 mb-3 bg-white/95 backdrop-blur border-b border-slate-200 flex items-center justify-between">
                <h3 className="font-semibold text-slate-700 text-sm">3層ナレッジ・ネットワーク</h3>
                <button
                  type="button"
                  onClick={() => setPanelOpen(false)}
                  className="text-slate-400 hover:text-slate-600"
                  aria-label="コントロールパネルを閉じる"
                >
                  ✕
                </button>
              </div>

              {/* 表示トグル */}
              <div className={panelSectionCls}>
                <label className="flex items-center gap-2 mb-1.5 text-slate-700">
                  <input
                    type="checkbox"
                    checked={hideIsolatedPosts}
                    onChange={(e) => setHideIsolatedPosts(e.target.checked)}
                  />
                  気づきと繋がっていない投稿を非表示
                </label>
                <label className="flex items-center gap-2 mb-1.5 text-slate-700">
                  <input
                    type="checkbox"
                    checked={excludeTestData}
                    onChange={(e) => setExcludeTestData(e.target.checked)}
                  />
                  テストデータを除外
                </label>
                <label className="flex items-center gap-2 text-slate-700">
                  <input
                    type="checkbox"
                    checked={showImage}
                    onChange={(e) => setShowImage(e.target.checked)}
                  />
                  ノード内に写真を表示
                </label>
              </div>

              {/* 日付フィルタ */}
              <div className={panelSectionCls}>
                <div className="font-semibold text-slate-500 mb-1.5">📅 日付フィルター</div>
                <label className="flex items-center gap-2 mb-1">
                  <span className="text-slate-500 w-10">開始</span>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="flex-1 px-1.5 py-0.5 border border-slate-300 rounded text-slate-700"
                  />
                </label>
                <label className="flex items-center gap-2">
                  <span className="text-slate-500 w-10">終了</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="flex-1 px-1.5 py-0.5 border border-slate-300 rounded text-slate-700"
                  />
                </label>
                {(startDate || endDate) && (
                  <button
                    type="button"
                    onClick={() => {
                      setStartDate('');
                      setEndDate('');
                    }}
                    className="mt-1.5 w-full py-1 bg-slate-100 border border-slate-300 rounded text-slate-600 hover:bg-slate-200"
                  >
                    日付フィルタをクリア
                  </button>
                )}
              </div>

              {/* 軸駆動フィルタ (master/dataSettings/M5 由来、マトリクス/分析画面と同じ) */}
              <div className={panelSectionCls}>
                <div className="font-semibold text-slate-500 mb-1.5">🔎 フィルタ</div>
                <AxisFilterPanel
                  filter={axisFilter}
                  open={showFilters}
                  onToggleOpen={() => setShowFilters((v) => !v)}
                  compact
                />
              </div>

              {/* 次数サイズ */}
              <div className={panelSectionCls}>
                <label className="flex items-center gap-2 text-slate-700 font-semibold">
                  <input
                    type="checkbox"
                    checked={useDegreeSize}
                    onChange={(e) => setUseDegreeSize(e.target.checked)}
                  />
                  リンク数でノードの大きさを変える
                </label>
                {useDegreeSize && (
                  <div className="mt-2 pl-1">
                    <div className="text-slate-500 mb-1">倍率: {degreeMultiplier.toFixed(1)}x</div>
                    <input
                      type="range"
                      min="0.1"
                      max="2.0"
                      step="0.1"
                      value={degreeMultiplier}
                      onChange={(e) => setDegreeMultiplier(parseFloat(e.target.value))}
                      className={sliderCls}
                    />
                  </div>
                )}
              </div>

              {/* 結実投稿強調 */}
              <div className="mb-3 p-2.5 rounded border border-amber-300 bg-amber-50">
                <label className="flex items-start gap-2 text-amber-700 font-semibold">
                  <input
                    type="checkbox"
                    checked={highlightIdeaPosts}
                    onChange={(e) => setHighlightIdeaPosts(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span>処方箋まで結実した投稿を強調</span>
                </label>
                <p className="mt-1 text-[10px] text-amber-700/80 leading-relaxed">
                  処方箋に繋がった投稿だけを明るく表示し、生まれた処方箋の数だけノードを大きくします。
                </p>
              </div>

              {/* グローバル強調フィルタ + 引力 */}
              <div className={panelSectionCls}>
                <div className="text-slate-500 mb-1.5">
                  グローバル強調フィルタ（対象以外が薄くなります）
                </div>
                {teamOptions.length > 0 && (
                  <div className="mb-2">
                    <select
                      value={highlightTeam}
                      onChange={(e) => setHighlightTeam(e.target.value)}
                      className={selectCls}
                    >
                      <option value="">チーム (すべて)</option>
                      {teamOptions.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                    <div className="flex items-center gap-2 mt-1 text-slate-400 text-[10px]">
                      <span>引力 {gravityTeam.toFixed(1)}</span>
                      <input
                        type="range"
                        min="0"
                        max="2"
                        step="0.1"
                        value={gravityTeam}
                        onChange={(e) => setGravityTeam(parseFloat(e.target.value))}
                        onPointerUp={() => setGravityTeam(0)}
                        className="flex-1 cursor-pointer accent-primary-600"
                      />
                    </div>
                  </div>
                )}
                {nicknameOptions.length > 0 && (
                  <div>
                    <select
                      value={highlightNickname}
                      onChange={(e) => setHighlightNickname(e.target.value)}
                      className={selectCls}
                    >
                      <option value="">ニックネーム (すべて)</option>
                      {nicknameOptions.map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                    <div className="flex items-center gap-2 mt-1 text-slate-400 text-[10px]">
                      <span>引力 {gravityNickname.toFixed(1)}</span>
                      <input
                        type="range"
                        min="0"
                        max="2"
                        step="0.1"
                        value={gravityNickname}
                        onChange={(e) => setGravityNickname(parseFloat(e.target.value))}
                        onPointerUp={() => setGravityNickname(0)}
                        className="flex-1 cursor-pointer accent-primary-600"
                      />
                    </div>
                  </div>
                )}
                {teamOptions.length === 0 && nicknameOptions.length === 0 && (
                  <div className="text-[10px] text-slate-400">
                    強調できる属性 (チーム / ニックネーム) がありません。
                  </div>
                )}
              </div>

              {/* 投稿 色分け + 凡例 */}
              <div className="mb-3">
                <div className="text-slate-500 mb-1">投稿（Post）の色分け</div>
                <select
                  value={postColorMode}
                  onChange={(e) => setPostColorMode(e.target.value)}
                  className={selectCls}
                >
                  {/* 動的軸 (master/dataSettings/M5 由来。どのモードでも意味のある色分け)。 */}
                  {axisFilter.axisOptions.length > 0 && (
                    <optgroup label="軸で色分け (動的)">
                      {axisFilter.axisOptions.map((o) => (
                        <option key={o.key} value={o.key}>
                          {o.label}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  <optgroup label="その他">
                    <option value="emotion">感情 (emotionTag)</option>
                    <option value="quadrant">HAPPY / HEALTHY</option>
                    <option value="categoryMajor">対象分類 (大分類)</option>
                    {scopeOptions.length > 0 && <option value="scope">わたし / 他者も</option>}
                    {durationOptions.length > 0 && (
                      <option value="duration">一時的 / 持続的</option>
                    )}
                    {teamOptions.length > 0 && <option value="team">チーム</option>}
                    {nicknameOptions.length > 0 && <option value="nickname">投稿者</option>}
                  </optgroup>
                </select>
                <PostLegend
                  mode={postColorMode}
                  emotionValues={collectEcwValues(posts, 'emotions')}
                  majorValues={collectCategoryMajorValues(posts, resolver)}
                  scopeValues={scopeOptions}
                  durationValues={durationOptions}
                  resolver={resolver}
                  axisGroups={axisFilter.groupsForAxis(postColorMode)}
                />
              </div>

              {/* 気づき 色分け + 凡例 */}
              <div className="mb-3">
                <div className="text-slate-500 mb-1">気づき（Insight）の色分け</div>
                <select
                  value={insightColorMode}
                  onChange={(e) => setInsightColorMode(e.target.value as InsightColorMode)}
                  className={selectCls}
                >
                  <option value="default">デフォルト (緑)</option>
                  <option value="emotion">感情 (emotionTag)</option>
                  <option value="sentiment">センチメント (赤〜青)</option>
                </select>
                {insightColorMode === 'default' && (
                  <div className="flex items-center gap-1.5 mt-2 text-slate-500">
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: INSIGHT_DEFAULT_COLOR }}
                    />
                    気づき (Insight)
                  </div>
                )}
                {insightColorMode === 'sentiment' && (
                  <div className="mt-2 space-y-1 text-slate-500">
                    <LegendDot color="rgb(255,100,100)" label="ポジティブ (赤)" />
                    <LegendDot color="rgb(200,200,200)" label="中間 (灰)" />
                    <LegendDot color="rgb(100,200,255)" label="ネガティブ (青)" />
                  </div>
                )}
              </div>

              {/* 選択ノード詳細 */}
              {selectedNode && (
                <div className="mt-3 p-2.5 bg-slate-50 rounded border border-slate-200">
                  <h4
                    className="font-semibold text-sm mb-2 leading-snug"
                    style={{ color: nodeDisplayColor(selectedNode) }}
                  >
                    {TYPE_LABEL[selectedNode.type]}: {selectedNode.label}
                  </h4>
                  {selectedNode.imageUrl && (
                    <img
                      src={selectedNode.imageUrl}
                      alt=""
                      className="w-full max-h-40 object-cover rounded mb-2"
                    />
                  )}
                  <p className="text-[11px] text-slate-600 whitespace-pre-wrap max-h-40 overflow-y-auto">
                    {selectedNode.description || '詳細なし'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* 使い方マニュアル modal */}
        {showManual && (
          <div className="absolute inset-0 z-20 flex items-center justify-center p-4">
            <button
              type="button"
              aria-label="閉じる"
              className="absolute inset-0 bg-slate-900/40"
              onClick={() => setShowManual(false)}
            />
            <div className="relative w-full max-w-lg max-h-[80%] overflow-y-auto bg-white rounded-xl shadow-2xl p-5 text-xs text-slate-600">
              <div className="flex items-center justify-between border-b border-slate-200 pb-2 mb-3">
                <h3 className="text-base font-semibold text-slate-800">
                  ネットワークグラフの使い方
                </h3>
                <button
                  type="button"
                  onClick={() => setShowManual(false)}
                  className="text-slate-400 hover:text-slate-600 text-lg leading-none"
                >
                  ✕
                </button>
              </div>
              <h4 className="text-sm font-semibold text-slate-700 border-l-4 border-primary-500 pl-2 mb-1.5">
                🔹 基本操作
              </h4>
              <ul className="list-disc pl-5 mb-3 space-y-1">
                <li>
                  <strong>ズーム＆パン:</strong> マウスホイール、またはスマートフォンでは 2
                  本指のピンチで拡大縮小、 背景ドラッグで視点を移動できます。
                </li>
                <li>
                  <strong>ノードのクリック:</strong>{' '}
                  クリックすると中心にズームし、パネル下部に詳細が表示されます
                  (投稿は詳細パネルを開きます)。
                </li>
                <li>
                  <strong>全体表示:</strong> 下部の「🔍 全体表示」で全体を初期表示に戻します。
                </li>
              </ul>
              <h4 className="text-sm font-semibold text-slate-700 border-l-4 border-amber-500 pl-2 mb-1.5">
                🔹 凡例とノードの形
              </h4>
              <ul className="list-disc pl-5 mb-3 space-y-1">
                <li>
                  <strong>■ 投稿 (Post):</strong> 四角形。現場の声や写真です。
                </li>
                <li>
                  <strong>◆ 気づき (Insight):</strong> ひし形。複数投稿から導いた洞察です。
                </li>
                <li>
                  <strong>● 処方箋 (Idea):</strong> 円形。気づきへの解決策です。
                </li>
              </ul>
              <h4 className="text-sm font-semibold text-slate-700 border-l-4 border-fuchsia-500 pl-2 mb-1.5">
                🔹 便利機能
              </h4>
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  <strong>レイアウト切替:</strong>{' '}
                  「整列（3列）」は投稿→気づき→処方箋を左から順に並べます。
                  数が多くて見にくいときは「円状（放射状）」に切り替えると、関連の強いノードが寄り集まり、
                  どこに投稿が集中しているかが分かりやすくなります（配置は力学で決まるため毎回少し変化します）。
                  「配置を固定」にチェックすると、その時点の配置のまま動きを止めます（チェックを外すと
                  同じ位置から動きを再開します）。
                </li>
                <li>
                  <strong>写真表示:</strong> ノード内に投稿写真 /
                  気づき・処方箋のキャッチ画像を表示します。
                </li>
                <li>
                  <strong>リンク数で大きさ:</strong>{' '}
                  つながりの多いノードを倍率に応じて大きくします。
                </li>
                <li>
                  <strong>強調フィルタ + 引力:</strong> 特定チーム /
                  投稿者を強調し、引力スライダーで同属性をクラスタ化します。
                </li>
                <li>
                  <strong>色分けの変更:</strong> 感情 / Happy×Healthy /
                  対象大分類などで塗り分けられます。
                </li>
                <li>
                  <strong>類似度リンク:</strong> 内容が似た「気づき」同士を青い点線で結びます。
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>

      <p className="px-4 py-2 text-[10px] text-slate-400 shrink-0 border-t border-slate-100">
        ※ 形状: 投稿=■ / 気づき=◆ /
        処方箋=●。青い線は「気づき」と元の「投稿」、橙の線は「処方箋」と元の「気づき」のつながりを表します。
        点線は内容が似た「気づき」同士を結びます（類似度 {similarityThreshold} 以上を最大{' '}
        {SIMILARITY_MAX_LINKS}{' '}
        件、表示の切り替え可）。ノードをタップすると拡大して詳しい内容を表示します。
      </p>
    </div>
  );
}

/** ノード形状 (投稿=■ / 気づき=◆ / 処方箋=●)。原点中心。 */
function NodeShape({
  type,
  r,
  fill,
  stroke,
  strokeWidth,
}: {
  type: NodeType;
  r: number;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
}) {
  const common = { fill, stroke, strokeWidth };
  if (type === 'post') {
    return <rect x={-r} y={-r} width={r * 2} height={r * 2} {...common} />;
  }
  if (type === 'insight') {
    const d = r * INSIGHT_DIAMOND_SCALE;
    return <polygon points={`0,${-d} ${d},0 0,${d} ${-d},0`} {...common} />;
  }
  return <circle r={r} {...common} />;
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <span
        className="inline-block w-2.5 h-2.5 rounded-full"
        style={{ backgroundColor: color }}
        aria-hidden
      />
      <span>{label}</span>
    </div>
  );
}

function PostLegend({
  mode,
  emotionValues,
  majorValues,
  scopeValues,
  durationValues,
  resolver,
  axisGroups = [],
}: {
  mode: string;
  emotionValues: string[];
  majorValues: string[];
  scopeValues: string[];
  durationValues: string[];
  resolver: ReturnType<typeof useAiCategoryMasters>;
  /** 動的軸モード時の bucket 凡例 (master/postField/M5 由来の色)。 */
  axisGroups?: AxisGroup[];
}) {
  // 動的軸モード (matrixAxisModel) のときは bucket 色凡例を出す (固定 case より優先)。
  if (axisGroups.length > 0) {
    return (
      <div className="mt-2 grid grid-cols-2 gap-1 text-slate-500">
        {axisGroups.slice(0, 12).map((g) => (
          <LegendDot key={g.code} color={g.color} label={g.label} />
        ))}
      </div>
    );
  }
  if (mode === 'emotion') {
    if (emotionValues.length === 0) return null;
    return (
      <div className="mt-2 grid grid-cols-2 gap-1 text-slate-500">
        {emotionValues.map((e) => (
          <LegendDot
            key={e}
            color={resolveEmotionColor(resolver, e) || POST_BASE_COLOR}
            label={e}
          />
        ))}
      </div>
    );
  }
  if (mode === 'quadrant') {
    return (
      <div className="mt-2 space-y-1 text-slate-500">
        {QUADRANT_LEGEND.map((q) => (
          <LegendDot key={q.label} color={q.color} label={q.label} />
        ))}
      </div>
    );
  }
  if (mode === 'categoryMajor') {
    if (majorValues.length === 0) return null;
    return (
      <div className="mt-2 space-y-1 text-slate-500">
        {majorValues.map((m) => (
          <LegendDot
            key={m}
            color={resolveCategoryMajorColor(resolver, m) || '#888'}
            label={`${m} ${resolveCategoryMajorLabel(resolver, m)}`}
          />
        ))}
      </div>
    );
  }
  if (mode === 'scope') {
    return (
      <div className="mt-2 space-y-1 text-slate-500">
        {scopeValues.map((v) => (
          <LegendDot key={v} color={stringToColor(v)} label={v} />
        ))}
      </div>
    );
  }
  if (mode === 'duration') {
    return (
      <div className="mt-2 space-y-1 text-slate-500">
        {durationValues.map((v) => (
          <LegendDot key={v} color={stringToColor(v)} label={v} />
        ))}
      </div>
    );
  }
  return (
    <div className="mt-2 text-[10px] text-slate-400">
      この項目は値ごとに自動で色が割り当てられます。
    </div>
  );
}
